import React, { useCallback, useEffect, useState, lazy } from 'react';
import { Minus, Plus, ShoppingBag, Loader2 } from 'lucide-react';
import { MenuItem, MenuItemOption, CartItemOption, ModifierOption, ResolvedModifierGroup } from '../../lib/types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { formatCurrency, cn } from '../../lib/utils';
import { useLanguage } from '../../lib/i18n';
import { useMenuItemOptions, useMenuItemModifiers } from '../../lib/hooks';
import { useMenuCategories } from '../../lib/menu-hooks';
import { supabase } from '../../lib/supabase';
/** Normalize string for accent-insensitive comparison */
function normalize(str: string) {
  return (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}
const PIZZA_ADDON_NAMES = ['extra queso', 'borde relleno'];
function isPizzaAddon(name: string): boolean {
  const n = (name || '').toLowerCase().trim();
  return PIZZA_ADDON_NAMES.some(addon => n.includes(addon));
}
function isChiniadita(name: string): boolean {
  return (name || '').toLowerCase().includes('chiniadita');
}
function is5Estaciones(name: string): boolean {
  const n = (name || '').toLowerCase();
  return n.includes('5 estaciones') || n.includes('cinco estaciones');
}
/** Emilius: the Siciliana pizza is not offered with seafood toppings. */
function isSicilianaPizza(name: string): boolean {
  return normalize(name).includes('siciliana');
}
function isSeafoodFlavor(name: string): boolean {
  const n = normalize(name);
  return n.includes('camaron') || n.includes('marisco');
}
/**
 * Emilius "5 Estaciones": each seafood station (camarones / mariscos)
 * adds this amount to the pizza. Two seafood stations add ₡4.000.
 */
const SEAFOOD_STATION_SURCHARGE = 2000;
/** Color map for Heladería Congelados ice cream flavors */
const ICE_CREAM_FLAVOR_COLORS: Record<string, string> = {
  'Naranja Holandesa': '#FF8C00',
  Vainilla: '#F5E6CA',
  Chocolate: '#5C3317',
  Pecaditos: '#C4956A',
  Unicornio: '#FF69B4',
  'Ron frutas': '#B8860B',
  'Ron pasas': '#8B6914',
  Café: '#6F4E37',
  Oreo: '#2C2C2C',
  Snickers: '#A0522D',
  'Fresa Mora': '#DC143C',
  'Choco chips': '#7B3F00',
  Churchill: '#B0E0E6',
  Pistacho: '#93C572',
  'Veteado de mango': '#FFD700',
  'Piña colada': '#FFF3B0',
  Mango: '#FF9933',
  'Piña colada (base agua)': '#FFF3B0',
  'Mango (base agua)': '#FF9933'
};
/** Check if a group name is an ice cream flavor group */
function isIceCreamFlavorGroup(groupName: string): boolean {
  return groupName.startsWith('🍧') || groupName.startsWith('🍨') || groupName.startsWith('🍦');
}
/** Strip embedded price from option names like "Caja — ₡500" → "Caja" */
function stripEmbeddedPrice(name: string): string {
  return name.replace(/\s*[—–-]\s*₡[\d.,\s]+$/, '').trim();
}
/** Safely resolve max_select: treat 0, null, undefined as 1 (radio/single-select) */
function safeMaxSelect(val: number | null | undefined): number {
  return val && val > 0 ? val : 1;
}
/** Get the free_qty for an option group (all options in a group share the same free_qty) */
function getGroupFreeQty(groupOptions: MenuItemOption[]): number {
  const val = groupOptions[0]?.free_qty;
  return val && val > 0 ? val : 0;
}
/** Calculate the effective price delta for an option based on how many free selections remain */
function getEffectivePriceDelta(option: MenuItemOption, freeQty: number, currentFreeUsed: number): number {
  if (freeQty > 0 && currentFreeUsed < freeQty) {
    return 0; // This selection is free
  }
  return Number(option.price_delta) || 0;
}
interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  onAdd: (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => void;
  primaryColor?: string;
  theme?: 'default' | 'capricho';
}
export function ProductDetailModal({
  isOpen,
  onClose,
  item,
  onAdd,
  primaryColor,
  theme = 'default'
}: ProductDetailModalProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, MenuItemOption[]>>({});
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, ModifierOption[]>>({});
  const [emiliusFlavors, setEmiliusFlavors] = useState<MenuItem[]>([]);
  const [emiliusFlavorsLoading, setEmiliusFlavorsLoading] = useState(false);
  const [selectedEmiliusFlavors, setSelectedEmiliusFlavors] = useState<MenuItem[]>([]);
  const [stationFlavors, setStationFlavors] = useState<MenuItem[]>([]);
  // Crack Pollo (id 20): hide the yellow +delta price label on the right.
  // Selection logic and totals are unchanged — only the visual delta is suppressed.
  const isCrackPollo = String(item?.restaurant_id) === '20';
  // Rancho San Francisco (id 28) — sellado contra errores: en Rancho,
  // TODO grupo de selección única (max_select <= 1) se trata como
  // OBLIGATORIO aunque el dato `is_required` venga en false. Esto evita
  // que un casado/refresco/natural/gaseosa se agregue sin su "con qué"
  // / sabor, garantizando que el detalle SIEMPRE quede capturado y se
  // muestre consistente en la comanda. Cero impacto en otros locales.
  const isRancho28 = String(item?.restaurant_id) === '28';
  // Emilius Pizza (id 27) — figure out whether this item lives in the
  // "Postres" category so we can hide the +delta on its size options
  // (Pequeña/Grande) without affecting savory pizzas, lomitos, etc.
  const {
    categories: emiliusCategories
  } = useMenuCategories(String(item?.restaurant_id) === '27' ? String(item.restaurant_id) : '');
  const emiliusCategoryName = (() => {
    if (String(item?.restaurant_id) !== '27' || !item?.category_id) return '';
    const cat = emiliusCategories.find(c => String(c.id) === String(item.category_id));
    return (cat?.name ?? '').toLowerCase();
  })();
  const isEmiliusPostre = emiliusCategoryName.includes('postre');
  // Desechables is priced by the required size selection. Hide only its base
  // price so customers see the final amount in the add-to-cart button instead.
  const isEmiliusDesechables = String(item?.restaurant_id) === '27' && item?.slug === 'desechables';
  // Emilius Pizza — items in the "Pizzas" category get the option deltas
  // rendered as the FULL absolute price (base + delta) instead of "+₡N".
  // e.g. Mediana shows "₡10 500" rather than "+₡3 500". The price in the
  // "Agregar al pedido" button keeps working as before.
  const isEmiliusPizza = emiliusCategoryName.includes('pizza');
  const {
    options,
    loading
  } = useMenuItemOptions(item?.id);
  const {
    modifierGroups,
    loading: modifiersLoading
  } = useMenuItemModifiers(item?.id, item ? {
    name: item.name,
    restaurant_id: item.restaurant_id
  } : undefined);
  // Reset state ONLY when modal opens for a different item
  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
      setSelectedModifiers({});
      setSelectedEmiliusFlavors([]);
      setStationFlavors([]);
    }
  }, [isOpen, item?.id]);
  if (!item) return null;
  // Group options by option_group
  const groupedOptions = options.reduce((acc, option) => {
    const group = option.option_group;
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(option);
    return acc;
  }, {} as Record<string, MenuItemOption[]>);
  const isEmilius = String(item?.restaurant_id) === '27';
  const estiloGroupKey = Object.keys(groupedOptions).find(k => normalize(k) === 'estilo');
  const hasEstiloGroup = isEmilius && !!estiloGroupKey;
  const selectedEstiloOption = hasEstiloGroup && estiloGroupKey ? selectedOptions[estiloGroupKey]?.[0] : null;
  const selectedEstiloName = selectedEstiloOption ? normalize(selectedEstiloOption.name) : '';
  const is1Ingrediente = selectedEstiloName.includes('1 ingrediente') || selectedEstiloName.includes('ingrediente');
  const is4Estaciones = selectedEstiloName.includes('4 estaciones') || selectedEstiloName.includes('estaciones');
  const isEmiliusStyle = selectedEstiloName === 'emilius';
  const requiredFlavorsCount = is1Ingrediente ? 1 : is4Estaciones ? 4 : 0;
  const needsFlavors = requiredFlavorsCount > 0;
  const is5EstacionesItem = isEmilius && /(^|\s)(5|cinco)\s*estaciones/i.test(item?.name || '');
  useEffect(() => {
    if (!isOpen || !hasEstiloGroup && !is5EstacionesItem) return;
    let cancelled = false;
    const fetchFlavors = async () => {
      setEmiliusFlavorsLoading(true);
      try {
        const {
          data: categories,
          error: catError
        } = await supabase.from('menu_categories').select('id, name').eq('restaurant_id', 27).ilike('name', '%pizza%').not('name', 'ilike', '%extra%');
        if (catError) throw catError;
        const categoryIds = categories.map(c => c.id);
        if (categoryIds.length === 0) {
          if (!cancelled) setEmiliusFlavors([]);
          return;
        }
        const {
          data: items,
          error: itemsError
        } = await supabase.from('menu_items').select('*').eq('restaurant_id', 27).eq('is_active', true).in('category_id', categoryIds).order('sort_order', {
          ascending: true
        });
        if (itemsError) throw itemsError;
        // Siciliana is not prepared with seafood, so those flavors are
        // excluded from its selector only. Every other pizza keeps the
        // full flavor list.
        const excludeSeafood = isSicilianaPizza(item?.name || '');
        const validFlavors = (items || []).filter(p => !isPizzaAddon(p.name) && !isChiniadita(p.name) && !is5Estaciones(p.name) && !(excludeSeafood && isSeafoodFlavor(p.name)) && p.id !== item?.id);
        if (!cancelled) setEmiliusFlavors(validFlavors);
      } catch (err) {
        console.error('[ProductDetailModal] fetch emilius flavors error:', err);
      } finally {
        if (!cancelled) setEmiliusFlavorsLoading(false);
      }
    };
    fetchFlavors();
    return () => {
      cancelled = true;
    };
  }, [isOpen, hasEstiloGroup, is5EstacionesItem, item?.id, item?.name]);
  useEffect(() => {
    setSelectedEmiliusFlavors([]);
  }, [selectedEstiloName]);
  /**
   * Check if a dependent option group should be visible based on its
   * parent group selection.
   *
   * Resolution order (generic — applies to any restaurant / any
   * product, NOT hardcoded a Cevicheo / Casado):
   *   1. Si el grupo NO tiene `depends_on_group`, siempre visible.
   *   2. Si el grupo tiene `depends_on_option_values` (text[]),
   *      sólo se muestra cuando el padre tiene seleccionada al menos
   *      una opción cuyo `name` aparece en esa lista (case-insensitive
   *      con trim — comparación robusta a variaciones tipográficas).
   *      Esto cubre el caso Casado → BBQ (sólo para Pollo Asado /
   *      Pollo a la Plancha).
   *   3. Fallback legacy: si el padre tiene cualquier selección que
   *      NO empiece con "no " o "sin " (español), el grupo se ve.
   *      Esto cubre el caso "¿Desea salsas?" → "Tipo de salsa":
   *      "Con salsas" muestra el dependiente; "Sin salsas" lo oculta.
   */
  function isGroupVisible(groupName: string, groupOptions: MenuItemOption[]): boolean {
    const dependsOn = groupOptions[0]?.depends_on_group;
    if (!dependsOn) return true; // No dependency, always visible
    // Defensive: if the parent group doesn't exist on this product (e.g.
    // it was renamed or removed), the dependency can never be satisfied
    // and the group would be invisible forever. Show it instead.
    if (!groupedOptions[dependsOn]) return true;
    const parentSelections = selectedOptions[dependsOn] || [];
    if (parentSelections.length === 0) return false; // Parent has no selection
    // Whitelist (preferred): explicit allowed parent option names
    const allowedValues = (groupOptions[0] as any)?.depends_on_option_values || null;
    if (Array.isArray(allowedValues) && allowedValues.length > 0) {
      const allowedNormalized = allowedValues.map((v: string) => String(v).trim().toLowerCase());
      // Defensive: if none of the whitelisted values exists in the parent
      // group, the condition can never be met and the group would be
      // invisible forever. Fall through to the legacy heuristic instead.
      const parentOptionNames = (groupedOptions[dependsOn] || []).map(o => String(o.name).trim().toLowerCase());
      const whitelistIsReachable = allowedNormalized.some(v => parentOptionNames.includes(v));
      if (whitelistIsReachable) {
        return parentSelections.some(sel => allowedNormalized.includes(String(sel.name).trim().toLowerCase()));
      }
    }
    // Legacy heuristic: hide if parent selection starts with a negative
    // marker — "no ..." or "sin ..." in español
    return !parentSelections.some(sel => {
      const n = sel.name.toLowerCase().trim();
      return n.startsWith('no ') || n.startsWith('sin ');
    });
  }
  // Emilius "Alitas": the included sauce must be chosen before the extra
  // sauces are offered. Scoped to this product only — every other item
  // keeps its existing group order.
  const isEmiliusAlitas = isEmilius && normalize(item?.name || '').includes('alita');
  // Sort option groups: ice cream flavors first, then everything else (toppings)
  const sortedGroupEntries = Object.entries(groupedOptions).sort(([a], [b]) => {
    const priority = (name: string) => {
      if (name.startsWith('🍦')) return 0; // Sabor de helado
      if (name.startsWith('🍧')) return 1; // Sabores de helado
      if (name.startsWith('🍨')) return 2; // Base agua
      if (isEmiliusAlitas) {
        const n = normalize(name);
        if (n.includes('salsa') && n.includes('incluida')) return 8;
        if (n.includes('salsa') && n.includes('extra')) return 9;
      }
      return 10; // Toppings and everything else last
    };
    return priority(a) - priority(b);
  });
  /**
   * Re-evaluate every dependent group of `parentGroupName` against a
   * tentative new selections map and CLEAR any dependent group that
   * is no longer visible. Generic — funciona para cualquier producto
   * con `depends_on_group` y/o `depends_on_option_values`. Esto cubre:
   *   • "¿Desea salsas?" → "Tipo de salsa" se limpia al cambiar a
   *     "Sin salsas"
   *   • Casado → "BBQ" se limpia al cambiar de Pollo Asado/Plancha a
   *     Costilla/Chicharrón/etc.
   */
  function clearHiddenDependents(parentGroupName: string, tentative: Record<string, MenuItemOption[]>): Record<string, MenuItemOption[]> {
    const next = {
      ...tentative
    };
    Object.entries(groupedOptions).forEach(([depGroupName, depGroupOpts]) => {
      if (depGroupOpts[0]?.depends_on_group !== parentGroupName) return;
      // Evaluate visibility using the tentative state instead of
      // committed state — we want to clear BEFORE the user sees it.
      const parentSelections = next[parentGroupName] || [];
      let visible = false;
      if (parentSelections.length > 0) {
        const allowedValues = (depGroupOpts[0] as any)?.depends_on_option_values || null;
        if (Array.isArray(allowedValues) && allowedValues.length > 0) {
          const allowedNormalized = allowedValues.map((v: string) => String(v).trim().toLowerCase());
          visible = parentSelections.some(sel => allowedNormalized.includes(String(sel.name).trim().toLowerCase()));
        } else {
          visible = !parentSelections.some(sel => {
            const n = sel.name.toLowerCase().trim();
            return n.startsWith('no ') || n.startsWith('sin ');
          });
        }
      }
      if (!visible) {
        next[depGroupName] = [];
      }
    });
    return next;
  }
  const handleOptionSelect = (groupName: string, option: MenuItemOption) => {
    const maxSel = safeMaxSelect(option.max_select);
    setSelectedOptions(prev => {
      const group = prev[groupName] || [];
      const isAlreadySelected = group.some(o => o.id === option.id);
      // Deselect if already selected
      if (isAlreadySelected) {
        const tentative = {
          ...prev,
          [groupName]: group.filter(o => o.id !== option.id)
        };
        return clearHiddenDependents(groupName, tentative);
      }
      // Radio behavior (single select): replace current selection
      if (maxSel === 1) {
        const tentative = {
          ...prev,
          [groupName]: [option]
        };
        return clearHiddenDependents(groupName, tentative);
      }
      // Multi-select: add if under limit
      if (group.length >= maxSel) {
        return prev; // at capacity
      }
      const tentative = {
        ...prev,
        [groupName]: [...group, option]
      };
      return clearHiddenDependents(groupName, tentative);
    });
  };
  const handleQuantityChange = (groupName: string, option: MenuItemOption, delta: number) => {
    const maxSel = safeMaxSelect(option.max_select);
    setSelectedOptions(prev => {
      const group = prev[groupName] || [];
      const currentQty = group.filter(o => o.id === option.id).length;
      const totalGroupQty = group.length;
      if (delta > 0) {
        // Check limits
        if (currentQty >= 2) return prev; // Cada salsa individual tiene máximo 2
        if (totalGroupQty >= maxSel) return prev; // El máximo total permitido del grupo
        return {
          ...prev,
          [groupName]: [...group, option]
        };
      } else {
        // Remove one instance
        if (currentQty === 0) return prev;
        const indexToRemove = group.findIndex(o => o.id === option.id);
        if (indexToRemove === -1) return prev;
        const newGroup = [...group];
        newGroup.splice(indexToRemove, 1);
        return {
          ...prev,
          [groupName]: newGroup
        };
      }
    });
  };
  const handleModifierSelect = (groupId: string, option: ModifierOption, maxSelect: number) => {
    const maxSel = safeMaxSelect(maxSelect);
    setSelectedModifiers(prev => {
      const group = prev[groupId] || [];
      const isAlreadySelected = group.some(o => o.id === option.id);
      if (isAlreadySelected) {
        return {
          ...prev,
          [groupId]: group.filter(o => o.id !== option.id)
        };
      }
      if (maxSel === 1) {
        return {
          ...prev,
          [groupId]: [option]
        };
      }
      if (group.length >= maxSel) {
        return prev;
      }
      return {
        ...prev,
        [groupId]: [...group, option]
      };
    });
  };
  const handleAdd = () => {
    const cartOptions: CartItemOption[] = [];
    // For option groups with free_qty, calculate effective prices
    // Only include options from visible groups
    Object.entries(selectedOptions).forEach(([groupName, group]) => {
      const groupOpts = groupedOptions[groupName] || [];
      if (!isGroupVisible(groupName, groupOpts)) return; // Skip hidden groups
      const freeQty = getGroupFreeQty(groupOpts);
      group.forEach((opt, index) => {
        const effectivePrice = freeQty > 0 && index < freeQty ? 0 : Number(opt.price_delta) || 0;
        cartOptions.push({
          option_name: opt.name,
          option_group: groupName,
          selection_mode: opt.selection_mode,
          price_delta: effectivePrice
        });
      });
    });
    Object.values(selectedModifiers).forEach(group => {
      group.forEach(opt => {
        cartOptions.push({
          option_name: opt.name,
          price_delta: Number(opt.price_delta) || 0
        });
      });
    });
    if (needsFlavors && selectedEmiliusFlavors.length > 0) {
      selectedEmiliusFlavors.forEach((flavor, i) => {
        cartOptions.push({
          option_name: flavor.name,
          option_group: requiredFlavorsCount === 1 ? 'Sabor' : `Sabor ${i + 1}/${requiredFlavorsCount}`,
          price_delta: 0
        });
      });
    }
    if (is5EstacionesItem) {
      stationFlavors.forEach((flavor, i) => {
        if (flavor) {
          cartOptions.push({
            option_name: flavor.name,
            option_group: `Estación ${i + 1}`,
            price_delta: isSeafoodFlavor(flavor.name) ? SEAFOOD_STATION_SURCHARGE : 0
          });
        }
      });
    }
    let finalNotes = notes;
    if (needsFlavors && selectedEmiliusFlavors.length > 0) {
      const flavorsNote = selectedEmiliusFlavors.map((f, i) => requiredFlavorsCount === 1 ? `Sabor: ${f.name}` : `Sabor ${i + 1}/${requiredFlavorsCount}: ${f.name}`).join('\n');
      finalNotes = finalNotes ? `${flavorsNote}\n\nNotas: ${finalNotes}` : flavorsNote;
    }
    if (is5EstacionesItem && stationFlavors.length > 0) {
      const stationsNote = stationFlavors.map((f, i) => `Estación ${i + 1}: ${f.name}`).join('\n');
      finalNotes = finalNotes ? `${stationsNote}\n\nNotas: ${finalNotes}` : stationsNote;
    }
    onAdd(item, quantity, cartOptions, finalNotes);
    onClose();
  };
  // Validation: required groups must have at least one selection (skip hidden dependent groups)
  const optionsValid = sortedGroupEntries.every(([groupName, groupOptions]) => {
    if (!isGroupVisible(groupName, groupOptions)) return true; // Hidden groups are always valid
    const firstOption = groupOptions[0];
    // Rancho (id 28): un grupo de selección única cuenta como requerido
    // aunque is_required no esté marcado en la base — sella el detalle
    // (ej. "con qué" del casado, sabor del natural). PERO los grupos de
    // EXTRAS / ADICIONALES (add-ons opcionales como "Agregar papas
    // caseras +₡700") NO deben obligar: el cajero debe poder agregar el
    // producto sin marcar un extra. Detectamos esos grupos por nombre
    // ("extra" / "adicional" / "agregar") y los dejamos opcionales.
    const groupNameNorm = normalize(groupName);
    const isOptionalExtraGroup = groupNameNorm.includes('extra') || groupNameNorm.includes('adicional') || groupNameNorm.includes('agregar');
    const requiredHere = firstOption.is_required || isRancho28 && safeMaxSelect(firstOption.max_select) <= 1 && !isOptionalExtraGroup;
    if (!requiredHere) return true;
    return (selectedOptions[groupName]?.length ?? 0) > 0;
  });
  const modifiersValid = modifierGroups.every(mg => {
    if (!mg.group.is_required) return true;
    return (selectedModifiers[mg.group.id]?.length ?? 0) >= (mg.group.min_select || 1);
  });
  const emiliusFlavorsValid = !needsFlavors || selectedEmiliusFlavors.length === requiredFlavorsCount;
  const stationsValid = !is5EstacionesItem || stationFlavors.length >= 1 && stationFlavors.length <= 5;
  const isValid = optionsValid && modifiersValid && emiliusFlavorsValid && stationsValid;
  const optionsTotal = Object.entries(selectedOptions).reduce((sum, [groupName, group]) => {
    const groupOpts = groupedOptions[groupName] || [];
    if (!isGroupVisible(groupName, groupOpts)) return sum; // Skip hidden groups
    const freeQty = getGroupFreeQty(groupOpts);
    return sum + group.reduce((gSum, opt, index) => {
      const effectivePrice = freeQty > 0 && index < freeQty ? 0 : Number(opt.price_delta) || 0;
      return gSum + effectivePrice;
    }, 0);
  }, 0);
  const modifiersTotal = Object.values(selectedModifiers).reduce((sum, group) => sum + group.reduce((gSum, opt) => gSum + (Number(opt.price_delta) || 0), 0), 0);
  // 5 Estaciones: every seafood station selected adds its own surcharge.
  const stationsSurcharge = is5EstacionesItem ? stationFlavors.filter(flavor => isSeafoodFlavor(flavor.name)).length * SEAFOOD_STATION_SURCHARGE : 0;
  const totalPrice = ((Number(item.price) || 0) + optionsTotal + modifiersTotal + stationsSurcharge) * quantity;
  const itemName = item.name;
  const itemDesc = item.description;
  return <Modal isOpen={isOpen} onClose={onClose} title={itemName} data-id="element-2969" data-mp="1igseic1iz6ls">
      <div className="flex flex-col h-[calc(100dvh-2rem)] sm:h-auto sm:max-h-[85vh]" data-id="element-2970" data-mp="1mjov5x7i8sn3">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-2971" data-mp="1ris1dkz814wn">
          {/* Header Image */}
          {item.image_url && Number(item.restaurant_id) !== 24 && <div className="relative w-full h-32 sm:h-64 bg-[#141414]" data-id="element-2972" data-mp="3ocwgpodpo6hf">
              <img src={item.image_url} alt={itemName} className="w-full h-full object-cover" loading="lazy" decoding="async" data-id="element-2973" data-mp="3taetxqyrh7v8" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] to-transparent" data-id="element-2974" data-mp="3gxkkm053q54c" />
            </div>}

          <div className="p-3 sm:p-6 space-y-3 sm:space-y-6" data-id="element-2975" data-mp="371r9ml716joo">
            {/* Title & Description */}
            <div data-id="element-2976" data-mp="8jlp0zm1klrd">
              <div className="flex justify-between items-start gap-4 mb-2" data-id="element-2977" data-mp="8k3vsq6byidj">
                <h2 className="text-xl sm:text-2xl font-black text-white leading-tight" style={{
                fontFamily: "'Playfair Display', serif"
              }} data-id="element-2978" data-mp="3l0p71apc43g">
                  {itemName}
                </h2>
                {!isEmiliusDesechables && <span className="text-lg sm:text-xl font-bold text-[#C9A34E] whitespace-nowrap" data-id="element-2979" data-mp="2jcy90bfgrnnq">
                    {formatCurrency(Number(item.price) || 0)}
                  </span>}
              </div>
              {itemDesc && <p className="text-white/60 leading-relaxed text-sm" data-id="element-2980" data-mp="2gpmeek2kon9c">
                  {itemDesc}
                </p>}
            </div>

            {/* ═══ OPTION GROUPS (menu_item_options) ═══ */}
            {sortedGroupEntries.filter(([groupName, groupOptions]) => isGroupVisible(groupName, groupOptions)).map(([groupName, groupOptions]) => {
            const firstOption = groupOptions[0];
            const isRequired = firstOption.is_required;
            const maxSel = safeMaxSelect(firstOption.max_select);
            const freeQty = getGroupFreeQty(groupOptions);
            const currentSelections = selectedOptions[groupName] || [];
            const selectedCount = currentSelections.length;
            // ─── Tier-style group? ──────────────────────────────────
            // When the group is a required, single-select selector
            // (size / portion / style) AND at least one option has a
            // price delta, we render the FULL price (base + delta)
            // next to each option instead of "+₡N". This applies to
            // every restaurant — multi-select toppings, optional
            // add-ons, and free-qty groups are not affected because
            // any of those conditions disqualify the group.
            const someOptionHasDelta = groupOptions.some(o => (Number(o.price_delta) || 0) !== 0);
            const groupShowsAbsolutePrice = isRequired && maxSel <= 1 && freeQty === 0 && someOptionHasDelta;
            const localizedGroupName = t(`group_${groupName}`) !== `group_${groupName}` ? t(`group_${groupName}`) : groupName;
            return <div key={groupName} className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-2981" data-mp="34inw3s8jk9tu">
                    <div className="flex items-baseline justify-between mb-2 sm:mb-4" data-id="element-2982" data-mp="38lkkjzkrj05p">
                      <div data-id="element-2983" data-mp="3dgq10ivebmme">
                        <h3 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-2984" data-mp="3ihnhmr0rc8ls">
                          {localizedGroupName}
                        </h3>
                        <p className="text-sm text-white/40" data-id="element-2985" data-mp="2kvyh2zxcxyrw">
                          {(() => {
                      // Build the body of the helper line first.
                      // For required single-select groups we keep
                      // the legacy "Requerido" only — saying
                      // "Selecciona hasta 1" is redundant.
                      let body: string | null = null;
                      if (freeQty > 0) {
                        body = `${freeQty} incluidas · Extra +${formatCurrency(Number(firstOption.price_delta) || 0)}`;
                      } else if (maxSel >= 20) {
                        body = 'Selecciona los que quieras';
                      } else if (maxSel > 1) {
                        body = `${t('select_up_to')} ${maxSel}`;
                      }
                      if (isRequired) {
                        return body ? `${t('required')} · ${body}` : t('required');
                      }
                      return body ?? `${t('select_up_to')} ${maxSel}`;
                    })()}
                        </p>
                        {freeQty > 0 && <p className="text-xs text-emerald-400/80 mt-0.5" data-id="element-2986" data-mp="11xaemdzrjsd0">
                            {selectedCount <= freeQty ? `${freeQty - selectedCount} gratis restante${freeQty - selectedCount !== 1 ? 's' : ''}` : `${selectedCount - freeQty} extra${selectedCount - freeQty !== 1 ? 's' : ''} (+${formatCurrency((selectedCount - freeQty) * (Number(firstOption.price_delta) || 0))})`}
                          </p>}
                      </div>
                      {isRequired && <span className="bg-[#C9A34E]/10 text-[#C9A34E] text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-2987" data-mp="2u0wgwxrrl9l0">
                          {t('required')}
                        </span>}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2" data-id="element-2988" data-mp="2f2kjgowkhwss">
                      {groupOptions.map(option => {
                  const isSelected = currentSelections.some(o => o.id === option.id);
                  // Only disable in multi-select when at capacity
                  const isDisabled = maxSel > 1 && !isSelected && selectedCount >= maxSel;
                  const flavorColor = isIceCreamFlavorGroup(groupName) && !option.name.toLowerCase().includes('cookie monster') ? ICE_CREAM_FLAVOR_COLORS[option.name] : undefined;
                  // Determine the display price for this option
                  const selectionIndex = isSelected ? currentSelections.findIndex(o => o.id === option.id) : selectedCount;
                  const wouldBeFree = freeQty > 0 && selectionIndex < freeQty;
                  const isQuantityMode = option.selection_mode === 'quantity';
                  const currentQty = currentSelections.filter(o => o.id === option.id).length;
                  if (isQuantityMode) {
                    return <div key={option.id} className={cn('w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all', currentQty > 0 ? 'border-[#C9A34E] bg-[#C9A34E]/5' : 'border-white/5 bg-white/5')} data-id="element-2989" data-mp="28gbcz5puryb0">
                              <div className="flex items-center gap-3" data-id="element-2990" data-mp="1kq79cycnzyk0">
                                <span className={cn('font-medium', currentQty > 0 ? 'text-white' : 'text-white/80')} data-id="element-2991" data-mp="1frw12s20pw3w">
                                  {stripEmbeddedPrice(option.name)}
                                </span>
                              </div>
                              <div className="flex items-center gap-3" data-id="element-2992" data-mp="3rfj37ozs3nbu">
                                {Number(option.price_delta) > 0 && <span className="text-sm font-bold text-[#C9A34E] shrink-0" data-id="element-2993" data-mp="8ewywbgk54h">
                                    +
                                    {formatCurrency(Number(option.price_delta))}
                                  </span>}
                                <div className="flex items-center gap-3 bg-[#0B0B0B] rounded-lg p-1 border border-white/10" data-id="element-2994" data-mp="38nyl5gx9uxco">
                                  <button type="button" disabled={currentQty === 0} onClick={() => handleQuantityChange(groupName, option, -1)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors" data-id="element-2995" data-mp="3dp5ql9bxzkg6">
                                    <Minus className="w-4 h-4" data-id="element-2996" data-mp="15ghgjtjlm1lh" />
                                  </button>
                                  <span className="w-4 text-center text-sm font-bold text-white" data-id="element-2997" data-mp="1j7cq781x8mq4">
                                    {currentQty}
                                  </span>
                                  <button type="button" disabled={isDisabled || currentQty >= 2} onClick={() => handleQuantityChange(groupName, option, 1)} className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed text-white transition-colors" data-id="element-2998" data-mp="1t5toyr73q5mq">
                                    <Plus className="w-4 h-4" data-id="element-2999" data-mp="22v8z1ddys75a" />
                                  </button>
                                </div>
                              </div>
                            </div>;
                  }
                  return <button key={option.id} type="button" disabled={isDisabled} onClick={() => handleOptionSelect(groupName, option)} className={cn('w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]/5' : 'border-white/5 hover:border-white/20 bg-white/5', isDisabled && 'opacity-50 cursor-not-allowed')} data-id="element-3000" data-mp="1f1qv03boa89h">
                            <div className="flex items-center gap-3" data-id="element-3001" data-mp="14zwwg1avyl13">
                              <div className={cn('w-4 h-4 sm:w-5 sm:h-5 border-2 flex items-center justify-center transition-colors shrink-0', maxSel > 1 || freeQty > 0 ? 'rounded-md' : 'rounded-full', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]' : 'border-white/20')} data-id="element-3002" data-mp="150of6bjdwo9y">
                                {isSelected && (maxSel > 1 || freeQty > 0 ? <svg className="w-3 h-3 text-[#0B0B0B]" viewBox="0 0 12 12" fill="none" data-id="element-3003" data-mp="29ythecir8sx8">
                                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3004" data-mp="2ixx3lpd7xofy" />
                                    </svg> : <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" data-id="element-3005" data-mp="30gezxri6a1hv" />)}
                              </div>
                              {flavorColor && <span className="w-5 h-5 sm:w-6 sm:h-6 rounded-full shrink-0 ring-1 ring-white/10" style={{
                        backgroundColor: flavorColor
                      }} data-id="element-3006" data-mp="3a5ymb753gdhh" />}
                              <span className={cn('font-medium', isSelected ? 'text-white' : 'text-white/80')} data-id="element-3007" data-mp="2rgik8fdrgcyy">
                                {stripEmbeddedPrice(option.name)}
                              </span>
                            </div>
                            {(() => {
                      // Emilius: groups marked as "incluido(s)" are
                      // already part of the dish. Hide only their
                      // inline price — extras/adicionales keep theirs.
                      // Cart calculations still use price_delta below.
                      const isEmiliusIncludedGroup = String(item?.restaurant_id) === '27' && normalize(groupName ?? '').includes('incluid');
                      const hideDelta = isCrackPollo || String(item?.restaurant_id) === '27' && (groupName ?? '').toLowerCase().trim() === 'personas' || isEmiliusPostre || isEmiliusIncludedGroup;
                      if (hideDelta) return null;
                      // GENERIC tier-style group (all restaurants):
                      // required + single-select + no free_qty +
                      // at least one priced option → show the FULL
                      // price (base + delta) per option instead of
                      // "+₡N". Selection logic and totals are
                      // unchanged downstream.
                      if (groupShowsAbsolutePrice) {
                        const basePrice = Number(item?.price) || 0;
                        const delta = Number(option.price_delta) || 0;
                        const total = basePrice + delta;
                        if (total <= 0) return null;
                        return <span className="text-sm font-bold text-[#C9A34E] shrink-0" data-id="element-3008" data-mp="3h3txslkt4a8l">
                                    {formatCurrency(total)}
                                  </span>;
                      }
                      // Emilius Pizza legacy branch — kept as a
                      // documented fallback. The generic check
                      // above catches this same case so this code
                      // path no longer fires in practice.
                      if (isEmiliusPizza && (groupName ?? '').toLowerCase().trim() === 'tamaño') {
                        const basePrice = Number(item?.price) || 0;
                        const delta = Number(option.price_delta) || 0;
                        const total = basePrice + delta;
                        if (total <= 0) return null;
                        return <span className="text-sm font-bold text-[#C9A34E] shrink-0" data-id="element-3009" data-mp="27v7f4f8j902t">
                                    {formatCurrency(total)}
                                  </span>;
                      }
                      if (freeQty > 0) {
                        if (isSelected && wouldBeFree) {
                          return <span className="text-xs font-bold text-emerald-400" data-id="element-3010" data-mp="1iii5h1apanok">
                                      Incluida
                                    </span>;
                        }
                        if (!isSelected && selectedCount < freeQty) {
                          return <span className="text-xs text-white/30" data-id="element-3011" data-mp="zmjo9fj6a9wb">
                                      Incluida
                                    </span>;
                        }
                        return <span className="text-sm font-bold text-[#C9A34E]" data-id="element-3012" data-mp="3vrkgjf68ag7s">
                                    +
                                    {formatCurrency(Number(option.price_delta) || 0)}
                                  </span>;
                      }
                      if (Number(option.price_delta ?? 0) !== 0) {
                        return <span className="text-sm font-bold text-[#C9A34E] shrink-0" data-id="element-3013" data-mp="380otevcpw96v">
                                    {Number(option.price_delta) > 0 ? `+${formatCurrency(Number(option.price_delta))}` : `-${formatCurrency(Math.abs(Number(option.price_delta)))}`}
                                  </span>;
                      }
                      return null;
                    })()}
                          </button>;
                })}
                    </div>

                    {groupName === estiloGroupKey && isEmiliusStyle && <div className="mt-3 p-3 sm:p-4 rounded-xl bg-[#6FA248]/10 border border-[#6FA248]/20" data-id="element-3014" data-mp="j94r95didwrb">
                        <p className="text-sm text-[#6FA248] font-medium leading-relaxed" data-id="element-3015" data-mp="o7fx750onn49">
                          <span className="font-bold" data-id="element-3016" data-mp="a7ofat71pmkz">Pizza Emilius:</span>{' '}
                          jamón, salami, pepperoni, carne molida, chile dulce,
                          cebolla, hongos.
                        </p>
                      </div>}

                    {groupName === estiloGroupKey && needsFlavors && <div className="mt-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-3017" data-mp="1upckuhaypxqu">
                        <div className="flex items-center justify-between mb-2" data-id="element-3018" data-mp="1zopjdzb74t99">
                          <h3 className="text-base sm:text-lg font-bold text-white" data-id="element-3019" data-mp="3k685ecng4dik">
                            {requiredFlavorsCount === 1 ? 'Elegí 1 sabor' : 'Elegí 4 sabores diferentes'}
                          </h3>
                          <span className={cn('text-xs font-black px-2 py-1 rounded-md', selectedEmiliusFlavors.length === requiredFlavorsCount ? 'bg-[#6FA248]/20 text-[#6FA248]' : 'bg-white/5 text-white/50')} data-id="element-3020" data-mp="3ty4cgd7ifdyx">
                            {selectedEmiliusFlavors.length}/
                            {requiredFlavorsCount}
                          </span>
                        </div>

                        {emiliusFlavorsLoading ? <div className="flex justify-center py-4" data-id="element-3021" data-mp="2djmd5wqpgtzj">
                            <Loader2 className="w-6 h-6 animate-spin text-[#6FA248]" data-id="element-3022" data-mp="336yuwirv4v7b" />
                          </div> : <div className="grid grid-cols-1 gap-2" data-id="element-3023" data-mp="379bzh3uvin2i">
                            {emiliusFlavors.map(pizza => {
                    const idx = selectedEmiliusFlavors.findIndex(f => f.id === pizza.id);
                    const isSelected = idx >= 0;
                    const atCap = !isSelected && selectedEmiliusFlavors.length >= requiredFlavorsCount;
                    return <button key={pizza.id} onClick={() => {
                      setSelectedEmiliusFlavors(prev => {
                        if (isSelected) return prev.filter(p => p.id !== pizza.id);
                        if (prev.length >= requiredFlavorsCount) {
                          if (requiredFlavorsCount === 1) return [pizza];
                          return prev;
                        }
                        return [...prev, pizza];
                      });
                    }} disabled={requiredFlavorsCount > 1 && atCap} className={cn('w-full flex items-center gap-3 p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#6FA248] bg-[#6FA248]/10' : atCap && requiredFlavorsCount > 1 ? 'border-white/5 bg-white/5 opacity-40 cursor-not-allowed' : 'border-white/5 bg-white/5 hover:border-white/20')} data-id="element-3024" data-mp="3smtpk347hdw">
                                  <div className={cn('w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center shrink-0', isSelected ? 'border-[#6FA248] bg-[#6FA248]' : 'border-white/20')} data-id="element-3025" data-mp="1z5qo0vim90ek">
                                    {isSelected && (requiredFlavorsCount > 1 ? <span className="text-[10px] font-black text-white" data-id="element-3026" data-mp="16h2z8pynrsw9">
                                          {idx + 1}
                                        </span> : <div className="w-2 h-2 bg-white rounded-full" data-id="element-3027" data-mp="180uusu3a7f9a" />)}
                                  </div>
                                  <span className={cn('font-medium text-sm sm:text-base', isSelected ? 'text-white' : 'text-white/80')} data-id="element-3028" data-mp="12zp2gxdj9u0a">
                                    {pizza.name}
                                  </span>
                                </button>;
                  })}
                          </div>}
                      </div>}
                  </div>;
          })}

            {/* Estaciones Picker — single multi-select group (hasta 5) */}
            {is5EstacionesItem && <div className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5 animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-3029" data-mp="2c2gc1gu1sv3g">
                <div className="flex items-baseline justify-between mb-2 sm:mb-4" data-id="element-3030" data-mp="34qlw01da2g28">
                  <div data-id="element-3031" data-mp="34o86e81d9w22">
                    <h3 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-3032" data-mp="2zrr8wn8a25cb">
                      Estaciones
                    </h3>
                    <p className="text-sm text-white/40" data-id="element-3033" data-mp="2vocolkdb32t2">Seleccione hasta 5</p>
                  </div>
                  <span className={cn('text-xs font-black px-2 py-1 rounded-md', stationFlavors.length > 0 ? 'bg-[#C9A34E]/10 text-[#C9A34E]' : 'bg-white/5 text-white/50')} data-id="element-3034" data-mp="2qqb54ybyx1h2">
                    {stationFlavors.length}/5
                  </span>
                </div>
                {emiliusFlavorsLoading ? <div className="flex justify-center py-6" data-id="element-3035" data-mp="3u1rlniu7hdx">
                    <Loader2 className="w-6 h-6 animate-spin text-[#C9A34E]" data-id="element-3036" data-mp="3sgx1xwivyg2" />
                  </div> : <div className="space-y-1.5 sm:space-y-2" data-id="element-3037" data-mp="dkd4w0oev2xh">
                    {emiliusFlavors.map(pizza => {
                const isSelected = stationFlavors.some(f => f.id === pizza.id);
                const atCap = !isSelected && stationFlavors.length >= 5;
                return <button key={pizza.id} type="button" disabled={atCap} onClick={() => {
                  setStationFlavors(prev => {
                    if (prev.some(f => f.id === pizza.id)) return prev.filter(f => f.id !== pizza.id);
                    if (prev.length >= 5) return prev;
                    return [...prev, pizza];
                  });
                }} className={cn('w-full flex items-center gap-3 p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]/5' : atCap ? 'border-white/5 bg-white/5 opacity-40 cursor-not-allowed' : 'border-white/5 hover:border-white/20 bg-white/5')} data-id="element-3038" data-mp="rhs0jn59c6xw">
                          <div className={cn('w-4 h-4 sm:w-5 sm:h-5 rounded-md border-2 flex items-center justify-center shrink-0', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]' : 'border-white/20')} data-id="element-3039" data-mp="x71hr82382la">
                            {isSelected && <svg className="w-3 h-3 text-[#0B0B0B]" viewBox="0 0 12 12" fill="none" data-id="element-3040" data-mp="1bs5j7aatgep2">
                                <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-3041" data-mp="12uv9htdncxm9" />
                              </svg>}
                          </div>
                          <span className={cn('font-medium text-sm sm:text-base', isSelected ? 'text-white' : 'text-white/80')} data-id="element-3042" data-mp="c24j1we85d6x">
                            {pizza.name}
                          </span>
                          {isSeafoodFlavor(pizza.name) && <span className="ml-auto shrink-0 text-sm font-bold text-[#C9A34E]" data-id="element-3043" data-mp="3phspabglh87g">
                              +{formatCurrency(SEAFOOD_STATION_SURCHARGE)}
                            </span>}
                        </button>;
              })}
                  </div>}
              </div>}

            {/* ═══ MODIFIER GROUPS (modifier_groups + modifier_options) ═══ */}
            {modifierGroups.length > 0 && modifierGroups.map(mg => {
            const maxSel = safeMaxSelect(mg.group.max_select);
            const currentSelections = selectedModifiers[mg.group.id] || [];
            const selectedCount = currentSelections.length;
            return <div key={mg.group.id} className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-3044" data-mp="31ct5am1rt56r">
                    <div className="flex items-baseline justify-between mb-2 sm:mb-4" data-id="element-3045" data-mp="28oduyctl3b1r">
                      <div data-id="element-3046" data-mp="28qqm9gl8exrh">
                        <h3 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-3047" data-mp="2dmyvyegntyr9">
                          {mg.group.name}
                        </h3>
                        {mg.group.description && <p className="text-sm text-white/40" data-id="element-3048" data-mp="2mof40fk05osb">
                            {mg.group.description}
                          </p>}
                        <p className="text-xs text-white/30 mt-1" data-id="element-3049" data-mp="1xxyn64k6vg7">
                          {mg.group.is_required ? `${t('required')} · ${t('select_up_to')} ${maxSel}` : `${t('select_up_to')} ${maxSel}`}
                        </p>
                      </div>
                      {mg.group.is_required && <span className="bg-[#C9A34E]/10 text-[#C9A34E] text-xs font-bold px-2 py-1 rounded-md uppercase tracking-wider" data-id="element-3050" data-mp="27ygzw2j3729k">
                          {t('required')}
                        </span>}
                    </div>

                    <div className="space-y-1.5 sm:space-y-2" data-id="element-3051" data-mp="1ac9nw7kge7n6">
                      {mg.options.map(option => {
                  const isSelected = currentSelections.some(o => o.id === option.id);
                  const isDisabled = maxSel > 1 && !isSelected && selectedCount >= maxSel;
                  return <button key={option.id} type="button" disabled={isDisabled} onClick={() => handleModifierSelect(mg.group.id, option, mg.group.max_select)} className={cn('w-full flex items-center justify-between p-2.5 sm:p-3 rounded-xl border transition-all text-left', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]/5' : 'border-white/5 hover:border-white/20 bg-white/5', isDisabled && 'opacity-50 cursor-not-allowed')} data-id="element-3052" data-mp="rk79z3z755gm">
                            <div className="flex items-center gap-3" data-id="element-3053" data-mp="26imie5m6ph9k">
                              <div className={cn('w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-colors shrink-0', isSelected ? 'border-[#C9A34E] bg-[#C9A34E]' : 'border-white/20')} data-id="element-3054" data-mp="2x86iz5t8fy8e">
                                {isSelected && <div className="w-2 h-2 bg-[#0B0B0B] rounded-full" data-id="element-3055" data-mp="3bxb4owz8kkl9" />}
                              </div>
                              <span className={cn('font-medium', isSelected ? 'text-white' : 'text-white/80')} data-id="element-3056" data-mp="374hye4o4d472">
                                {stripEmbeddedPrice(option.name)}
                              </span>
                            </div>
                            {isCrackPollo || String(item?.restaurant_id) === '27' && normalize(mg.group.name).includes('incluid') ? null : (() => {
                      const delta = Number(option.price_delta ?? 0);
                      if (delta > 0) return <span className="text-sm font-bold text-[#C9A34E]" data-id="element-3057" data-mp="tyuve9nulkfr">
                                        +{formatCurrency(delta)}
                                      </span>;
                      if (delta < 0) return <span className="text-sm font-bold text-emerald-400" data-id="element-3058" data-mp="18qdh8o3e2971">
                                        -{formatCurrency(Math.abs(delta))}
                                      </span>;
                      return <span className="text-xs text-white/30" data-id="element-3059" data-mp="2h5uifpkgfzo1">
                                      {t('included') !== 'included' ? t('included') : 'Incluido'}
                                    </span>;
                    })()}
                          </button>;
                })}
                    </div>
                  </div>;
          })}

            {/* Notes */}
            <div className="bg-[#141414] rounded-2xl p-3 sm:p-5 border border-white/5" data-id="element-3060" data-mp="39tqd6oqgrhi6">
              <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3" data-id="element-3061" data-mp="3dvlg1xo1m2y2">
                {t('checkout_notes')}
              </h3>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('notes_placeholder')} className="resize-none h-16 sm:h-24 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#C9A34E] focus:ring-[#C9A34E]" data-id="element-3062" data-mp="3ivg4lukcigfy" />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-3 sm:p-6 bg-[#0B0B0B] border-t border-white/10 mt-auto" data-id="element-3063" data-mp="t9eat7x7gan0">
          <div className="flex items-center gap-3 sm:gap-4" data-id="element-3064" data-mp="1pwj0hf9zpwea">
            {/* Quantity Selector */}
            <div className="flex items-center bg-[#141414] rounded-xl border border-white/10 p-1" data-id="element-3065" data-mp="1lxihnwriitra">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-id="element-3066" data-mp="1h47i8gp15f9e">
                <Minus className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-3067" data-mp="24utgxhd59n6q" />
              </button>
              <span className="w-8 sm:w-12 text-center font-bold text-base sm:text-lg text-white" data-id="element-3068" data-mp="2nrllns63ajoe">
                {quantity}
              </span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-id="element-3069" data-mp="2spwuq0ok8q5k">
                <Plus className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-3070" data-mp="250axhirr0l07" />
              </button>
            </div>

            {/* Add to Cart Button */}
            <Button onClick={handleAdd} disabled={!isValid} className="flex-1 h-12 sm:h-14 text-base sm:text-lg font-bold bg-[#C9A34E] hover:bg-[#B8923D] text-[#0B0B0B]" data-id="element-3071" data-mp="2a0qc0dnrnyjr">
              <div className="flex items-center justify-between w-full px-2" data-id="element-3072" data-mp="203tz7vqu4q9e">
                <span data-id="element-3073" data-mp="3qeorf7vd1k2h">{t('add_to_cart')}</span>
                <span data-id="element-3074" data-mp="3ufiejz0zauub">{formatCurrency(totalPrice)}</span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Modal>;
}